import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataTableDataSearchDto,
  ManualDefinitionTableDataValue,
  TableRowDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  BasicMathPlugin,
  Button,
  ConditionalFilterPlugin,
  convertToDisplayValues,
  convertToStorageValues,
  dateDraftConfig,
  DateValidationPlugin,
  ErrorMessage,
  FetchOptionsFunction,
  GoogleFontIcon,
  jsonEditorWidgetsWrapper,
  Modal,
  MultiSelectOption,
  SumExtendedPlugin,
} from '@asset-metrix/portal-ui';
import { useTimeout } from '@mantine/hooks';
import {
  JsonError,
  JsonSchema,
  useEditor,
  useEditorPlugin,
  Widget,
} from '@sagold/react-json-editor';
import {
  MutationStatus,
  useIsFetching,
  useQueryClient,
} from '@tanstack/react-query';
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { z } from 'zod';

import {
  fetchDocUploadModalValues,
  ModalValues,
} from '../../api/portalGateway/docviewUploadFormValues';
import { useEffectiveGlobalSelections } from '../../store/hooks/useEffectiveGlobalSelections';
import { useSelections } from '../../store/hooks/useSelections';
import { pixelSizeHelperPortalUi } from '../../utils/pixelSizeHelperPortalUi';

import {
  addMultiValueAsDataValue,
  addMultiValueAsOptions,
} from './addMultiValueOptions';
import { getInitialData } from './getInitialData';
import { isInvestmentAssumptionForm } from './investmentAssumptionModalHelpers';

import styles from './updateTableRowModal.module.scss';

const MULTI_VALUE_PLACEHOLDER = 'Multiple values *';

// ConditionalFilterPlugin fetches with an empty filter first and then again with the
// row selection. Keep loading up across that gap to avoid a flickering form.
const FILTER_OPTIONS_LOADING_HOLD_MS = 100;

const UserDataType = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.array(z.string()),
  z.array(z.number()),
  z.array(z.boolean()),
]);
type UserDataType = z.infer<typeof UserDataType>;

export type UpdateTableRowModalComponentProps = {
  onClose: () => void;
  opened: boolean;
  isDocViewWidget: boolean;
  onApply: (data: TableRowDto['data'], context: TableRowDto['context']) => void;
  jsonSchema: JsonSchema;
  schemaType: string;
  data: TableRowDto['data'];
  updateRowStatus: MutationStatus;
  errorMessage?: string;
  selectedRowCount: number;
  fetchConditionalFilterOptions: FetchOptionsFunction;
  initialModalValues: ModalValues | null;
  loadingFilterOptions: boolean;
  pageFilterSelections: DataTableDataSearchDto['selection'];
  customWidth?: number;
};

function hasSupportedDataType(value: unknown): value is UserDataType {
  return UserDataType.safeParse(value).success;
}

const propertyHasMultiValueSelected = (
  value: ManualDefinitionTableDataValue,
  propertyDefinition: JsonSchema
) => {
  if (isPropertyTypeString(propertyDefinition)) {
    if (
      propertyDefinition.options?.placeholder === MULTI_VALUE_PLACEHOLDER &&
      !value
    ) {
      return true;
    }

    return false;
  }

  if (Array.isArray(value)) {
    return value?.includes(MULTI_VALUE_PLACEHOLDER);
  }

  return false;
};

// Check if property value is present in the initial dropdown values
const propertyValueIsPresentInDropdownValues = (
  value: ManualDefinitionTableDataValue,
  propertyDefinition: JsonSchema,
  initialModalValues: ModalValues | null
) => {
  const dataProperty = propertyDefinition?.conditionalFilter?.dataProperty;
  if (dataProperty && initialModalValues && Array.isArray(value)) {
    // Empty array is technically always included in the modal values
    if (value.length === 0) {
      return true;
    }

    if (
      typeof value[0] === 'string' &&
      initialModalValues[dataProperty].values.includes(value[0])
    ) {
      return true;
    }

    return false;
  }
  return true;
};

// Exclude Multi Values * from getting sorted in the dropdown
const customSortCallback = (a: MultiSelectOption, b: MultiSelectOption) => {
  const aSelected = a.value === MULTI_VALUE_PLACEHOLDER;
  const bSelected = b.value === MULTI_VALUE_PLACEHOLDER;
  // selected entries should be placed before unselected ones
  if (aSelected && !bSelected) {
    return -1;
  }
  if (!aSelected && bSelected) {
    return 1;
  }

  return (a.label ?? String(a.value)).localeCompare(b.label ?? String(b.value));
};

export function UpdateTableRowModalComponent({
  onClose,
  opened,
  isDocViewWidget,
  onApply,
  jsonSchema,
  data,
  updateRowStatus,
  errorMessage,
  selectedRowCount,
  fetchConditionalFilterOptions,
  initialModalValues,
  loadingFilterOptions,
  pageFilterSelections,
  schemaType,
  customWidth,
}: UpdateTableRowModalComponentProps) {
  const initialData = useMemo(() => {
    const rawData = getInitialData(jsonSchema, data);
    // Transform initial data to display values (multiply by factor)
    // e.g., backend value 0.1234 with factor 100 becomes 12.34 for display
    return convertToDisplayValues(rawData, jsonSchema) as typeof rawData;
  }, [jsonSchema, data]);
  const [editorData, setEditorData] = useState(initialData);
  const [validationErrors, setValidationErrors] = useState<JsonError[]>([]);
  const { t } = useTranslation();
  const [conditionalFilterError, setConditionalFilterOptionsError] = useState<
    string | undefined
  >();
  const errorMessageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (updateRowStatus !== 'error') {
      return;
    }
    errorMessageRef.current?.scrollIntoView({ block: 'nearest' });
  }, [updateRowStatus, errorMessage]);

  const editor = useEditor({
    data: initialData,
    widgets: jsonEditorWidgetsWrapper(customSortCallback),
    schema: jsonSchema,
    onChange: (data, _root, editor) => {
      // reset conditional filter error if user changes any dropdown selection
      setConditionalFilterOptionsError(undefined);
      setValidationErrors(editor.getErrors());
      /**
       * Handle reset of values in case of multi-select
       */
      const tempData = structuredClone(data);
      let forceRerender = false; // Flag to determine if editor setData is required
      for (const key in tempData) {
        // Remove Multi Values * option if other options selected
        if (
          Array.isArray(tempData[key]) &&
          tempData[key].length > 1 &&
          tempData[key].includes(MULTI_VALUE_PLACEHOLDER)
        ) {
          // Condition to check if Multi Values * was checked or not
          tempData[key] =
            tempData[key].indexOf(MULTI_VALUE_PLACEHOLDER) ===
            tempData[key].length - 1
              ? [MULTI_VALUE_PLACEHOLDER]
              : tempData[key].filter(
                  (item) => item !== MULTI_VALUE_PLACEHOLDER
                );
          forceRerender = true;
        }

        // Reset the value if Multi Values * is selected, but its not there in the options
        let keyOption;
        if (_root.type === 'object') {
          keyOption = _root.children.find((item) => item.property === key);
        }
        const keyEnums = keyOption?.options?.enum;
        if (
          Array.isArray(tempData[key]) &&
          tempData[key].includes(MULTI_VALUE_PLACEHOLDER) &&
          keyEnums &&
          Array.isArray(keyEnums) &&
          !keyEnums.includes(MULTI_VALUE_PLACEHOLDER)
        ) {
          forceRerender = true;
          delete tempData[key];
        }
      }
      // Force editor data update
      if (forceRerender) {
        editor.setData(tempData);
      }
      setEditorData(tempData);
    },
    validate: false,
    addOptionalProps: false,
    draftConfig: dateDraftConfig,
    plugins: [BasicMathPlugin, SumExtendedPlugin, DateValidationPlugin],
  });

  useEditorPlugin(editor, ConditionalFilterPlugin, {
    fetchOptions: fetchConditionalFilterOptions,
  });

  const apply = useCallback(() => {
    if (validationErrors.length > 0) {
      console.error(
        'Cannot submit update table row modal due to validation errors.',
        validationErrors
      );
      return;
    }

    if (isInvestmentAssumptionForm(schemaType)) {
      // for investment modals, just send the editor data
      onApply(editorData, { pageFilterSelections });
      return;
    }

    const changedData: TableRowDto['data'] = {};

    // Filter for editable properties and supported data type:
    // Data for read only field could crash portal api
    // Typeguard should only be needed to make TS happy
    for (const [key, value] of editorData != null
      ? Object.entries(editorData)
      : []) {
      const propertyDefinition = jsonSchema.properties?.[key];
      if (
        propertyDefinition != null &&
        typeof propertyDefinition != 'boolean' &&
        !propertyDefinition?.options?.readOnly &&
        hasSupportedDataType(value) &&
        !propertyHasMultiValueSelected(value, propertyDefinition) &&
        propertyValueIsPresentInDropdownValues(
          value,
          propertyDefinition,
          initialModalValues
        )
      ) {
        changedData[key] = value;
      }
    }

    // before uploading file check if any of the changedData field has selectAll in jsonSchema
    // if yes then show error
    for (const changedDataKey in changedData) {
      if (
        typeof jsonSchema.properties?.[changedDataKey] === 'object' &&
        jsonSchema.properties?.[changedDataKey]?.options?.selectAll &&
        Array.isArray(changedData[changedDataKey]) &&
        changedData[changedDataKey].length > 10
      ) {
        const error = t('app.upload.hideAndSelectAllFieldError', {
          title: jsonSchema.properties?.[changedDataKey]?.title ?? '',
        });
        setConditionalFilterOptionsError(error);
        console.error(error);
        return;
      }
    }

    // Convert to storage values before submission
    const transformedData = convertToStorageValues(changedData, jsonSchema);
    onApply(transformedData as TableRowDto['data'], { pageFilterSelections });
  }, [
    editorData,
    initialModalValues,
    jsonSchema,
    onApply,
    t,
    validationErrors,
    pageFilterSelections,
    schemaType,
  ]);

  const ModalTitle = isDocViewWidget
    ? t('app.updateTableRow.titleForTypeDoc', { count: selectedRowCount })
    : t('app.updateTableRow.title', { count: selectedRowCount });

  return (
    <Modal
      actions={
        <>
          <Button
            variant="secondary"
            icon={<GoogleFontIcon name="close" />}
            onClick={() => onClose()}
          >
            {t('app.updateTableRow.cancel')}
          </Button>
          <Button
            disabled={validationErrors.length > 0 || loadingFilterOptions}
            loading={updateRowStatus === 'pending'}
            variant="primary"
            onClick={apply}
          >
            {t('app.updateTableRow.submit')}
          </Button>
        </>
      }
      modalType="action"
      opened={opened}
      onClose={() => onClose()}
      size={pixelSizeHelperPortalUi(customWidth || 650)}
      title={ModalTitle}
    >
      <div
        className={clsx(
          styles['content'],
          'portal-form rje-form rje-theme rje-theme--light'
        )}
      >
        <Widget
          editor={editor}
          options={{
            disabled: updateRowStatus === 'pending' || loadingFilterOptions,
            loading: loadingFilterOptions,
          }}
        />
        {conditionalFilterError && (
          <ErrorMessage className={styles['message']}>
            {conditionalFilterError}
          </ErrorMessage>
        )}
        {updateRowStatus === 'error' && (
          <div ref={errorMessageRef}>
            <ErrorMessage className={styles['message']}>
              {errorMessage ??
                t('app.updateTableRow.updateError', {
                  count: selectedRowCount,
                })}
            </ErrorMessage>
          </div>
        )}
      </div>
    </Modal>
  );
}

const isPropertyTypeString = (property: JsonSchema) => {
  if (
    property.type?.length &&
    (property.type.includes('string') || property.type.includes('number'))
  ) {
    return true;
  }

  return property.type === 'string';
};

export type UpdateTableRowModalProps = {
  contentId: string;
  instanceId: string;
  data: {
    rowId: string;
    data: TableRowDto['data'];
    file_name?: string;
  }[];
  errorMessage?: string;
  jsonSchema: JsonSchema;
  schemaType: string;
  onApply: (data: TableRowDto['data'], context: TableRowDto['context']) => void;
  onClose: () => void;
  opened: boolean;
  updateRowStatus: MutationStatus;
};

export function UpdateTableRowModal({
  contentId,
  instanceId,
  ...props
}: UpdateTableRowModalProps) {
  const queryClient = useQueryClient();
  const selectedRowCount = props.data.length;
  const [editableData, setEditableData] = useState({});
  const [editableJsonSchema, setEditableJsonSchema] = useState(
    props.jsonSchema
  );
  const [isDocViewWidget, setIsDocViewWidget] = useState(false);
  const [initialModalValues, setInitialModalValues] =
    useState<ModalValues | null>(null);
  const isFetchingFilterOptions =
    useIsFetching({ queryKey: ['tableModalValues'] }) > 0;
  const [isLoadingHeld, setIsLoadingHeld] = useState(false);
  const { start: startLoadingHold, clear: clearLoadingHold } = useTimeout(
    () => setIsLoadingHeld(false),
    FILTER_OPTIONS_LOADING_HOLD_MS
  );

  useEffect(() => {
    if (isFetchingFilterOptions) {
      clearLoadingHold();
      setIsLoadingHeld(true);
      return;
    }

    startLoadingHold();
  }, [clearLoadingHold, isFetchingFilterOptions, startLoadingHold]);

  const loadingFilterOptions = isFetchingFilterOptions || isLoadingHeld;
  const globalFilterSelections = useEffectiveGlobalSelections();
  const instanceFilterSelections = useSelections(instanceId);
  const pageFilterSelections = useMemo(
    () => ({ ...globalFilterSelections, ...instanceFilterSelections }),
    [globalFilterSelections, instanceFilterSelections]
  );

  const fetchConditionalFilterOptions = useCallback<FetchOptionsFunction>(
    async (filter) => {
      // Prevent calling the modalValues endpoint when the filter value is present but the inital values are not yet fetched
      if (!initialModalValues && !isEmpty(filter)) {
        return {};
      }

      // Remove 'Multiple Values *' from filters
      const clonedFilters = structuredClone(filter);
      for (const key in filter) {
        if (filter[key]?.includes(MULTI_VALUE_PLACEHOLDER)) {
          delete clonedFilters[key];
        }

        // Remove filter values not present in initial modal values
        if (
          initialModalValues &&
          filter[key] != null &&
          Array.isArray(filter[key]) &&
          !initialModalValues[key].values.includes(filter[key][0])
        ) {
          delete clonedFilters[key];
        }
      }
      const data = await queryClient.fetchQuery({
        queryKey: [
          'tableModalValues',
          {
            contentId,
            filter: clonedFilters,
            context: { pageFilterSelections },
          },
        ],
        queryFn: fetchDocUploadModalValues,
      });

      if (!data) {
        return {};
      }

      // Store initial modal values for comparison later
      if (!initialModalValues) {
        setInitialModalValues(structuredClone(data));
      }
      // Add 'Multi Values *' options in dropdown
      addMultiValueAsOptions(editableJsonSchema, data, props.data);

      return data;
    },
    [
      contentId,
      editableJsonSchema,
      initialModalValues,
      props.data,
      queryClient,
      pageFilterSelections,
    ]
  );

  const customWidth = useMemo(
    () =>
      typeof editableJsonSchema.options?.modalWidth === 'number'
        ? editableJsonSchema.options?.modalWidth
        : undefined,
    [editableJsonSchema]
  );

  useEffect(() => {
    // Single row selection case
    if (props.data.length === 1) {
      setEditableData(props.data[0].data);
      setEditableJsonSchema(props.jsonSchema);
    } else {
      // Add 'Multiple Values *' as a property value
      const { updatedData, updatedSchema } = addMultiValueAsDataValue(
        props.jsonSchema,
        props.data
      );
      setEditableData(updatedData);
      setEditableJsonSchema(updatedSchema);
    }
    setIsDocViewWidget(!!props.data?.[0]?.file_name);
  }, [props.data, props.jsonSchema]);

  return (
    <UpdateTableRowModalComponent
      fetchConditionalFilterOptions={fetchConditionalFilterOptions}
      selectedRowCount={selectedRowCount}
      {...props}
      jsonSchema={editableJsonSchema}
      data={editableData}
      isDocViewWidget={isDocViewWidget}
      initialModalValues={initialModalValues}
      loadingFilterOptions={loadingFilterOptions}
      pageFilterSelections={pageFilterSelections}
      customWidth={customWidth}
    />
  );
}
